<script setup lang="ts">
import LogoIcon from "@/components/icons/navBarIcons/LogoIcon.vue";
import Notifications from "@/components/navBar/notifications/Notifications.vue";
import NavBarSearchField from "@/components/inputs/NavBarSearchField.vue";
import Approvals from "@/components/navBar/approvals/Approvals.vue";
import ChangeLanguage from "@/components/navBar/languages/ChangeLanguage.vue";
import UserDimensions from "@/components/navBar/dimensions/UserDimensions.vue";
import UserSection from "@/components/navBar/userSection/UserSection.vue";
import {computed, ref, watch} from "vue";
import UserSlider from "@/components/navBar/userSection/UserSlider.vue";
import UserIcon from "@/components/icons/UserIcon.vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {storeToRefs} from "pinia";
import {Dialog} from "quasar";
import AboutNamaDialog from "@/components/dialogs/AboutNamaDialog.vue";
import AiBot from "@/components/navBar/aiassistant/AiBot.vue"
import ClientCache from "@/utils/login/ClientCache.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import {useRoute} from "vue-router";
import RouteNames from "@/router/RouteNames.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import ProgressController from "@/components/progress/ProgressController.vue";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import Chat from "@/components/navBar/chat/Chat.vue";
import SupportDateAlerts from "@/components/settings/SupportDateAlerts.vue";

let emit = defineEmits(['toggleSideBar']);

const {isSideBarOpen, compactMode, isUltra} = storeToRefs(useNamaFlagsStore());
const namaFlagsStore = useNamaFlagsStore();
const isSliderVisible = ref<boolean>(false);
const navRef = ref<HTMLElement | null>(null);
const route = useRoute();
let {tasksCount} = storeToRefs(useLoginInfoStore());

watch(() => navRef.value, () => {
  namaFlagsStore.onNavBarHeightChanged(navRef.value!.offsetHeight);
})

function openSlider() {
  isSliderVisible.value = !isSliderVisible.value;
}

async function showAboutDialog() {
  Dialog.create({
    component: AboutNamaDialog
  })
}
const canShowNavBarBorder = computed(() => {
  return route.name != RouteNames.WELCOME_ROUTE;
})

</script>

<template>
  <nav
    style="height:var(--navbar-height)"
    :class="[canShowNavBarBorder?'dark:tw-border-[#6C757D] tw-border-gray-300':'tw-border-namaBackground-deep-light dark:tw-border-namaBackground-deep-dark']"
    ref="navRef" class="tw-content-center tw-w-screen tw-h-fit tw-items-center tw-relative tw-px-3 compact:tw-px-2 ultra:tw-px-1.5 tw-py-1.5 compact:tw-py-1 ultra:tw-py-0.5 tw-border-b-[0.25px] tw-bg-namaBackground-deep-light
        dark:tw-bg-namaBackground-deep-dark">
    <div class="tw-flex tw-justify-between tw-items-center tw-gap-5 "
         :class="{'laptop:tw-gap-2':isSideBarOpen}">
      <div
        class="tw-flex-none tw-flex tw-w-fit tw-justify-start tw-items-center tw-pl-1.5 ultra:tw-pl-1 tw-gap-6 compact:tw-gap-4 ultra:tw-gap-3 mobile:tw-gap-5">
        <NamaIcon default-icon="nm-menu" id="sideBarBtn" class="tw-size-5 ultra:tw-size-4 tw-cursor-pointer" @click.stop="emit('toggleSideBar')"/>
        <a href="#" @contextmenu.prevent="showAboutDialog">
          <LogoIcon has-title class="tw-h-7 ultra:tw-h-6" is-dark-light/>
        </a>
      </div>
      <div class="tw-basis-1/3 mobile:tw-hidden">
        <NavBarSearchField/>
      </div>
      <q-space/>
      <div class="mobile:tw-hidden">
        <SupportDateAlerts />
      </div>
      <q-space class="mobile:tw-hidden"/>
      <div class="tw-flex-none tw-flex tw-w-fit tw-justify-end tw-items-center tw-gap-5 ultra:tw-gap-4">
        <ProgressController v-if="tasksCount > 0"/>
        <div v-if="ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.aiModuleAvailable && CurrentUserUtils.userCanUseAI()">
          <AiBot/>
        </div>
        <div v-if="CurrentUserUtils.userCanUseChat()">
          <Chat/>
        </div>
        <Notifications/>
        <Approvals/>
        <UserDimensions class="mobile:tw-hidden"/>
        <ChangeLanguage class="mobile:tw-hidden"/>
        <UserSection class="mobile:tw-hidden"/>
        <div class="tw-flex tw-p-1 desktop:tw-hidden">
          <a @click="openSlider()" class="hover:tw-cursor-pointer">
            <div v-if="!isSliderVisible">
              <q-avatar rounded class="tw-size-8 ultra:tw-size-7 dark:tw-bg-gray-600 tw-bg-namaBackground-dim-light tw-rounded-full">
                <img v-if="LoginUtils.userImageSrc.value" :src="LoginUtils.userImageSrc.value" alt="user"/>
                <UserIcon v-else class="tw-size-6 ultra:tw-size-5"/>
              </q-avatar>
            </div>
            <div v-if="isSliderVisible">
              <q-avatar rounded class="tw-size-8 ultra:tw-size-7 dark:tw-bg-gray-600 tw-bg-gray-200 tw-rounded-full">
                <NamaIcon default-icon="close" class="tw-size-5 ultra:tw-size-4"/>
              </q-avatar>
            </div>
          </a>
        </div>
      </div>
    </div>
    <UserSlider :isVisible="isSliderVisible"/>
  </nav>
</template>

<style scoped>

</style>
